import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import type { ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { StudioActionError } from '../../studio/StudioActionError';
import { profileHasReadyFile } from '../profileSlots';
import { profileMessages as m } from '../profileMessages';
import { useProfileZip } from '../useProfileZip';
import { sendAllMessages } from '../sendAll/sendAllMessages';
import { ReleasePickerDialog } from './ReleasePickerDialog';

interface ProfileHeadActionsProps {
  accountId: number;
  profile: ArtistProfileResponse;
  /** Opens "Send to all" (design profile-card c). */
  onSendAll: () => void;
}

/**
 * The buttons of the page head (design a1, profile-card c): "Use a release", "Send to all" and
 * "Download all" (off while nothing is ready).
 */
const ProfileHeadActionsComponent: React.FC<ProfileHeadActionsProps> = ({ accountId, profile, onSendAll }) => {
  const intl = useIntl();
  const [pickerOpen, setPickerOpen] = useState(false);
  const zip = useProfileZip(accountId);
  const hasFile = profileHasReadyFile(profile.assets);

  return (
    <div className="d-flex flex-wrap gap-2 align-items-center" data-testid="profile-head-actions">
      <SMLButton onClick={() => setPickerOpen(true)}>{intl.formatMessage(m.useRelease)}</SMLButton>
      <SMLButton onClick={onSendAll}>
        <span data-testid="profile-send-all">{intl.formatMessage(sendAllMessages.open)}</span>
      </SMLButton>
      <SMLButton variant="primary" disabled={!hasFile || zip.isBusy} onClick={zip.download}>
        {intl.formatMessage(zip.isBusy ? m.downloadAllBusy : m.downloadAll)}
      </SMLButton>
      <StudioActionError feedback={zip.feedback} />
      <ReleasePickerDialog
        accountId={accountId}
        isOpen={pickerOpen}
        initialUuid={profile.newestRelease?.catalogItemUuid}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
};

export const ProfileHeadActions = TypedMemo(ProfileHeadActionsComponent);
